import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  useGetAgencyDashboardLeadsCountQuery,
  useGetAgencyDashboardOverdueCountQuery,
} from "../../../redux/api/authApi";
import { RightSection } from "./RightSection";
import { All_LEADS_ROUTE } from "../../../constants/routes";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import BaseCard from "../../BaseCard/BaseCard";

export default function LeadsStages({ agencyId }) {
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [showList, setShowList] = useState(true);
  const [date, setDate] = useState({
    fromDate: new Date(),
    toDate: new Date(),
  });

  const methods = useForm();
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [filter, setFilter] = useState("all");

  const agentId = useWatch({
    control: methods.control,
    name: "agentid",
  });
  const selectedAgentId = !selectAll && agentId ? agentId : null;

  const {
    data: leadData,
    refetch,
    isFetching,
    isSuccess,
  } = useGetAgencyDashboardLeadsCountQuery({
    agcId: agencyId,
    agtId: selectedAgentId,
    toDate: filter === "all" ? "" : date.toDate.toISOString().slice(0, 10),
    fromDate: filter === "all" ? "" : date.fromDate.toISOString().slice(0, 10),
  });

  const { data: overDueData } = useGetAgencyDashboardOverdueCountQuery(
    {
      toDate: date.toDate.toISOString(),
      agcId: agencyId,
      agtId: selectedAgentId,
    },
    { skip: filter !== "Overdue" }
  );

  useEffect(() => {
    if (selectAll) methods.setValue("agentid", null);
    refetch();
  }, [selectAll, methods, refetch]);

  const handleFilterDataAccordingToDate = (filterType) => {
    const today = new Date();
    setFilter(filterType);

    if (filterType === "today") {
      setDate({ fromDate: today, toDate: today });
    } else if (filterType === "custom") {
      setDate((prev) => ({
        fromDate: prev.fromDate || today,
        toDate: prev.toDate || today,
      }));
    } else if (filterType === "Overdue") {
      setDate({ fromDate: today, toDate: today });
    }
  };

  useEffect(() => {
    if (filter === "Overdue" && overDueData) {
      setData(overDueData);
    } else if (leadData) {
      setData(leadData);
    } else {
      setData([]);
    }
  }, [filter, leadData, overDueData]);

  return (
    <>
      <BaseCard title="Leads" sx={{ flex: 1, minHeight: 520 }}>
        {/* Loader */}
        {isFetching ? (
          <Box
            sx={{
              height: 440,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : !isSuccess ? (
          <Box
            sx={{
              height: 440,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">No data found</Typography>
          </Box>
        ) : (
          <>
            {/* Filter Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              {[
                { label: "Leads Stages", type: "all" },
                { label: "Today's Leads", type: "today" },
                { label: "Date Range", type: "custom" },
                { label: "Overdue", type: "Overdue" },
              ].map((btn) => (
                <Button
                  key={btn.type}
                  fullWidth
                  size="small"
                  variant={filter === btn.type ? "contained" : "outlined"}
                  onClick={() => {
                    handleFilterDataAccordingToDate(btn.type);
                    setShowDateRangePicker(btn.type === "custom");
                    setShowList(true);
                  }}
                >
                  {btn.label}
                </Button>
              ))}
            </Stack>

            {/* Fixed height container (keeps layout stable) */}
            <Box
              sx={{
                height: 440,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Date Range Picker (slides open without changing total height) */}
              <Box
                sx={{
                  height: showDateRangePicker ? 70 : 0,
                  overflow: "hidden",
                  transition: "height 0.3s ease",
                  flexShrink: 0,
                }}
              >
                {showDateRangePicker && (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems="center"
                      justifyContent="space-between"
                      my={1}
                      width="100%"
                      gap={1}
                    >
                      <Box
                        width={{ xs: "100%", sm: "80%" }}
                        display="flex"
                        gap={1}
                      >
                        <DatePicker
                          label="From Date"
                          value={date.fromDate}
                          onChange={(newValue) =>
                            setDate((prev) => ({
                              ...prev,
                              fromDate: newValue,
                            }))
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                        <DatePicker
                          label="To Date"
                          value={date.toDate}
                          onChange={(newValue) =>
                            setDate((prev) => ({ ...prev, toDate: newValue }))
                          }
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                          }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          height: 40,
                          width: { xs: "100%", sm: "20%" },
                        }}
                        onClick={() => {
                          setShowList(true);
                          setFilter("custom");
                          refetch();
                        }}
                      >
                        Apply
                      </Button>
                    </Stack>
                  </LocalizationProvider>
                )}
              </Box>

              {/* List (only one scrollbar, height auto-adjusts inside container) */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 1,
                  scrollbarGutter: "stable",
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": { width: "10px" },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#b0b0b0",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#909090" },
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: "#b0b0b0 #f1f1f1",
                }}
              >
                {showList && (
                  <List dense>
                    {[...(data || [])]
                      .filter((t) => t.statusid !== 8 && t.statusid !== 28)
                      .sort((a, b) => b.leadCount - a.leadCount)
                      .map((task) => (
                        <ListItem
                          key={task.statusid}
                          disableGutters
                          divider
                          sx={{ py: 0.5 }}
                        >
                          <Link
                            to={{
                              pathname: All_LEADS_ROUTE,
                              search: `?status=${task.statusid}&filter=${filter}${
                                selectedAgentId
                                  ? `&agentid=${selectedAgentId}`
                                  : ""
                              }${
                                filter === "custom"
                                  ? `&fromDate=${date.fromDate
                                      .toISOString()
                                      .slice(0, 10)}&toDate=${date.toDate
                                      .toISOString()
                                      .slice(0, 10)}`
                                  : ""
                              }`,
                            }}
                            style={{
                              width: "100%",
                              textDecoration: "none",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                >
                                  <Typography fontSize={13}>
                                    {task.Status}
                                  </Typography>
                                  <Typography
                                    fontSize={13}
                                    color="text.secondary"
                                  >
                                    ({task.leadCount} leads)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Link>
                        </ListItem>
                      ))}
                  </List>
                )}
              </Box>
            </Box>
          </>
        )}
      </BaseCard>

      {/* Right Section */}
      <BaseCard title="Leads by Priority" sx={{ flex: 1, minHeight: 520 }}>
        <FormProvider {...methods}>
          <RightSection
            agencyId={agencyId}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            leadData={leadData}
          />
        </FormProvider>
      </BaseCard>
    </>
  );
}
